import { forwardRef } from 'react';
import mergeRefs from 'merge-refs';
import { useFormContext } from '@leagueplatform/web-common';
import { useRequiredErrorMessage } from 'hooks/use-required-error-message.hook';
import { GDSTextInputProps, TextInput } from '@leagueplatform/genesis-core';
import { ariaDescribedbyProp } from 'common/utils/get-ariadescribedby-prop';
import { RHFInputWrapper } from 'components/form-elements/rhf-input-wrapper.component';
import type { MergeWithRHFInputWrapper } from 'components/form-elements/rhf-input-wrapper.component';

export const RHFTextInput = forwardRef<
  HTMLInputElement,
  MergeWithRHFInputWrapper<GDSTextInputProps>
>(
  (
    {
      type,
      id,
      label,
      hint,
      required,
      requiredMessage,
      registerOptions,
      onChange,
      ...restComponentProps
    },
    ref,
  ) => {
    const {
      register,
      formState: { errors },
    } = useFormContext();

    const requiredErrorMessage = useRequiredErrorMessage(label);

    const { ref: RHFRegisterRef, ...restRHFRegisterProps } = register(id, {
      ...registerOptions,
      required: required && (requiredMessage || requiredErrorMessage),
      ...(onChange && { onChange }),
    });

    return (
      <RHFInputWrapper id={id} label={label} hint={hint} required={required}>
        <TextInput
          type={type}
          id={id}
          {...(errors?.[id]?.message && { inputStatus: 'error' })}
          {...ariaDescribedbyProp({
            id,
            error: !!errors?.[id]?.message,
            hint,
          })}
          {...restComponentProps}
          {...restRHFRegisterProps}
          ref={mergeRefs(RHFRegisterRef, ref)}
        />
      </RHFInputWrapper>
    );
  },
);
